import { HttpHeaders } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { OrderListParameters, OrderListRequestScreen } from 'src/app/model/request/order-list-screen-request';
import { OrderDetailsScreen } from 'src/app/model/screen/order-details-screen';
import { ApiService } from 'src/app/service/api.service';
import { LocalAssetReaderService } from 'src/app/service/local-asset-reader.service';
import { SandboxPageStateService } from 'src/app/service/sandbox-page-state.service';
import { XmlToJsonParserService } from 'src/app/service/xml-to-json-parser.service';

import { OrderDetailsService } from './order-detail-service';

@Component({
  selector: 'app-order-details-screen',
  templateUrl: './order-details-screen.component.html',
  styleUrls: ['./order-details-screen.component.scss']
})
export class OrderDetailsScreenComponent implements OnInit {
  constructor(
    private apiService: ApiService,
    private xmlToJsonParserService: XmlToJsonParserService,
    private localAssetReaderService: LocalAssetReaderService,
    private sandboxPageStateService: SandboxPageStateService,
    private formBuilder: FormBuilder,
    private orderDetailsService: OrderDetailsService
  ) { }

  private orderlistScreenRequest: OrderListRequestScreen = {} as OrderListRequestScreen;
  inputFormDetails = this.formBuilder.group({
    fromDateTime: ['', [Validators.required]],
    toDateTime: ['', [Validators.required]],
    parameters: ['NONE'],
    searchPNR: ['']
  });

  orderDetailsScreen: OrderDetailsScreen = {} as OrderDetailsScreen;
  submitted: boolean = false;
  startValue: number = 0;
  itemsPerPage: number = 20;
  totalPages: number = 0;
  pages: number[] = [];
  pageLengthList: number[] = [20, 30, 40, 50];
  displayMatchedOrders: any[] = [];
  parameters: string[] = ['NONE', 'TICKETED', 'UNTICKETED'];

  ngOnInit(): void {
    this.loadSavedState();
    const savedFormValues = sessionStorage.getItem('orderFormValues');
    if (savedFormValues) {
      const parsedFormValues = JSON.parse(savedFormValues);
      this.inputFormDetails.setValue(parsedFormValues);
    } else {
      this.inputFormDetails.setValue({
        fromDateTime: '',
        toDateTime: '',
        parameters: 'NONE',
        searchPNR: ''
      });
    }
    const savedOrderDetails = sessionStorage.getItem('orderDetails');
    if (savedOrderDetails) {
      this.orderDetailsScreen = JSON.parse(savedOrderDetails);
      this.displayMatchedOrders = this.orderDetailsScreen.matchedOrders || [];
    }
    this.fetchPages();
  }

  loadSavedState(): void {
    const savedOrderDetails = this.orderDetailsService.getOrderDetails();
    if (savedOrderDetails) {
      this.orderDetailsScreen = savedOrderDetails;
      if (savedOrderDetails.matchedOrders) {
        this.displayMatchedOrders = savedOrderDetails.matchedOrders;
      }
    }
  }

  private async initOrderDetailsScreenRequest() {
    const apiProfileConnection = this.sandboxPageStateService.getActiveAPIConnectionProfile();

    let jsonRequestTemplate = await this.localAssetReaderService.getContent(this.sandboxPageStateService.ORDER_DETAILS_REQUEST_TEMPLATE);
    this.orderlistScreenRequest = JSON.parse(jsonRequestTemplate as any);

    let party = this.orderlistScreenRequest['soapenv:Envelope']['soapenv:Body'].IATA_OrderListRQ.Party;
    party.Participant.Aggregator.AggregatorID = apiProfileConnection.aggregatorID;
    party.Participant.Aggregator.Name = apiProfileConnection.aggregatorName;
    party.Sender.TravelAgency.AgencyID = apiProfileConnection.agencyName;
    party.Sender.TravelAgency.Name = apiProfileConnection.agencyName;
    party.Sender.TravelAgency.IATA_Number = apiProfileConnection.iata;
  }

  initFormValues(): boolean {
    let request = this.orderlistScreenRequest['soapenv:Envelope']['soapenv:Body'].IATA_OrderListRQ.Request;
    if (this.inputFormDetails.valid) {
      let fromDateTime: any = this.inputFormDetails.value.fromDateTime;
      let toDateTime: any = this.inputFormDetails.value.toDateTime;
      let convertedFromDateTime = this.convertLocalTimeToUTC(fromDateTime);
      let convertedToDateTime = this.convertLocalTimeToUTC(toDateTime);
      request.OrderFilterCriteria.CreationDateTimeFilterCriteria.FromDateTime = convertedFromDateTime;
      request.OrderFilterCriteria.CreationDateTimeFilterCriteria.ToDateTime = convertedToDateTime;
      let remarkText = this.inputFormDetails.value.parameters;
      if (remarkText !== 'NONE') {
        request.OrderListParameters = {} as OrderListParameters;
        if (remarkText === 'TICKETED') {
          request.OrderListParameters!.Remark = {
            DisplayInd: true,
            RemarkText: 'TICKETED'
          };
        } else {
          request.OrderListParameters!.Remark = {
            DisplayInd: false,
            RemarkText: 'TICKETED'
          };
        }
      } else {
        delete request.OrderListParameters;
      }
      return true;
    }
    return false;
  }

  convertLocalTimeToUTC(dateTime: any): string {
    let date = new Date(dateTime);
    return date.toISOString().substring(0, 19);
  }

  async showResults() {
    this.onFormSubmit();
    this.submitted = true;
    await this.initOrderDetailsScreenRequest();
    if (this.initFormValues()) {
      let url = this.sandboxPageStateService.ORDER_LIST_API_URL;
      const response = await this.apiService.getXMLResponse(url, JSON.stringify(this.orderlistScreenRequest), new Map(), {
        headers: new HttpHeaders({
          soapAction: 'OrderList19_1_V1'
        }),
        responseType: 'text'
      });
      this.orderDetailsService.setOrderDetails(response);

      this.updateOrderListScreenXML(url);
      this.fetchPages();
      this.submitted = false;
    } else if (this.inputFormDetails.value.fromDateTime && this.inputFormDetails.value.toDateTime) {
      this.submitted = false;
    }
  }

  onFormSubmit(): void {
    sessionStorage.setItem('orderFormValues', JSON.stringify(this.inputFormDetails.value));
    this.orderDetailsService.setFormValues(this.inputFormDetails.value);
  }

  isTotNum: boolean = false;
  fetchPages() {
    this.pages = [];
    this.displayMatchedOrders = [];
    let results: any[] = this.orderDetailsScreen.matchedOrders;
    if (results && results.length > 0) {
      let totalCount = results.length;
      this.totalPages = totalCount / this.itemsPerPage;
      if (this.totalPages % 1 != 0) {
        this.totalPages = Math.ceil(this.totalPages);
      }
      let startButton: number = 0;
      let endButton: number = 0;
      if (this.startValue === 0) {
        endButton = this.startValue + 4;
      } else {
        startButton = this.startValue - 1;
        endButton = this.startValue + 3;
      }
      if (endButton >= this.totalPages) {
        endButton = this.totalPages;
        if (this.totalPages >= 4) {
          startButton = endButton - 4;
        } else {
          startButton = 0;
        }
      }
      for (let i = startButton; i < endButton; i++) {
        this.pages.push(i);
      }

      let startFrom = this.startValue * this.itemsPerPage;
      let endsWith = startFrom + this.itemsPerPage;
      for (let i = startFrom; i < endsWith; i++) {
        if (results[i] !== undefined) {
          this.displayMatchedOrders.push(results[i]);
        }
      }
    }
    this.isTotNum = true;
  }

  showPage(value: any) {
    this.startValue = value;
    this.fetchPages();
  }

  next() {
    if (this.startValue < this.totalPages - 1) {
      this.startValue++;
      this.fetchPages();
    }
  }

  previous() {
    if (this.startValue > 0) {
      this.startValue--;
      this.fetchPages();
    }
  }

  updatePageLength(updatedLength: any) {
    this.startValue = 0;
    this.itemsPerPage = parseInt(updatedLength);
    this.fetchPages();
    this.updateFormDetails();
  }

  updateFormDetails() {
    this.orderDetailsScreen.formDetail = {
      fromDateTime: this.inputFormDetails.value.fromDateTime as string,
      toDateTime: this.inputFormDetails.value.toDateTime as string,
      parameters: this.inputFormDetails.value.parameters as string,
      searchPNR: this.inputFormDetails.value.searchPNR as string,
      activePageLength: this.itemsPerPage
    };
  }

  updateOrderListScreenXML(url: string) {
    let patchSandboxSourceData: any = '';
    if (this.sandboxPageStateService.getErrorMessage() !== '') {
      if (this.sandboxPageStateService.getErrorMessage().includes('No orders matching filter criteria')) {
        this.orderDetailsScreen.matchedOrders = [];
      }
      patchSandboxSourceData = { showError: 'Yes', errorMessage: this.sandboxPageStateService.getErrorMessage() };
      this.sandboxPageStateService.updateSandboxDataSource(patchSandboxSourceData);
    } else {
      this.orderDetailsScreen.xml = this.sandboxPageStateService.getResponseFromMap(url);
      this.orderDetailsScreen.json = this.xmlToJsonParserService.parseXMLToJSON(this.orderDetailsScreen.xml!);
      this.mapOrderDetails(this.orderDetailsScreen.json);
      sessionStorage.setItem('orderDetails', JSON.stringify(this.orderDetailsScreen));
      if (this.sandboxPageStateService.getWarningMessage() !== '') {
        patchSandboxSourceData = { showWarning: 'Yes', warningMessage: this.sandboxPageStateService.getWarningMessage() };
        this.sandboxPageStateService.updateSandboxDataSource(patchSandboxSourceData);
      }
      this.sandboxPageStateService.updateOrderDetailsScreenData(this.orderDetailsScreen);
    }
  }

  mapOrderDetails(json: any) {
    let response = json['SOAP:ENVELOPE']['SOAP:BODY'][0]['NS2:IATA_ORDERLISTRS'][0]['NS2:RESPONSE'];
    if (response) {
      let matchedOrder = response[0]['NS2:MATCHEDORDER'];
      if (matchedOrder && matchedOrder.length > 0) {
        this.orderDetailsScreen.matchedOrders = [];
        this.orderDetailsScreen.matchedOrders = matchedOrder;
        this.orderDetailsScreen.copyMatchedOrders = matchedOrder;
      }
    }
    this.orderDetailsScreen.matchedOrders.sort((a, b) => b['NS2:CREATIONDATETIME'][0].localeCompare(a['NS2:CREATIONDATETIME'][0]));
  }

  formateDateTime(givenDate: any) {
    let date = new Date(givenDate);
    const options: Intl.DateTimeFormatOptions = {
      dateStyle: 'medium',
      timeStyle: 'medium'
    };
    return date.toLocaleString('en-GB', options);
  }

  getSearchedPNR() {
    let searchPNRValue = this.inputFormDetails.value.searchPNR;
    if (searchPNRValue && searchPNRValue !== '') {
      let searchPnr = searchPNRValue.toUpperCase();
      let filteredPNR = this.orderDetailsScreen.matchedOrders.find((pnr) => pnr['NS2:ORDERREFID'][0] === searchPnr);
      if (filteredPNR !== undefined) {
        this.orderDetailsScreen.matchedOrders = [];
        this.orderDetailsScreen.matchedOrders = [filteredPNR];
        this.startValue = 0;
      }
    } else {
      this.orderDetailsScreen.matchedOrders = this.orderDetailsScreen.copyMatchedOrders;
    }
    this.fetchPages();
  }

  findPNRDetails(orders: any) {
    if (orders && orders['NS2:ORDERREFID']) {
      let selectedOrderId = orders['NS2:ORDERREFID'][0];
      let findOrderScreenData = this.sandboxPageStateService.getFindOrderScreenData();
      findOrderScreenData.pnr = selectedOrderId;
      findOrderScreenData.isOrderDetailsTriggered = true;
      this.sandboxPageStateService.updateFindOrderScreen(findOrderScreenData);
      this.sandboxPageStateService.getYourBookingScreenData();

      let patchSandboxSourceData: any = { mainMenu: 'Yes', sandboxScreen: 'your-booking-screen' };
      this.sandboxPageStateService.updateSandboxDataSource(patchSandboxSourceData);
      this.updateFormDetails();
    }
  }

  orderListScreenCloseClick() {
    this.updateFormDetails();
    let patchSandboxSourceData = { hideSideMenu: 'Yes', mainMenu: 'none' };
    this.sandboxPageStateService.updateSandboxDataSource(patchSandboxSourceData);
  }
}
